import template from './rating_and_feedback.html';
import * as Utils from '../../lib/savanna_utils.js';

class RatingAndFeedbackViewModel {
    constructor(dialog) {
        this.dialog = dialog;
        this.performance_cycle_id = ko.observable();
        this.date = ko.observable(moment().format('YYYY-MM-DD hh:mm'));
        this.companies = ko.observableArray();
        this.selected_company = ko.observable();
        this.description = ko.observable();
        this.from_date = ko.observable();
        this.to_date = ko.observable();
        this.company_id = ko.observable();
        this.editable = ko.observable(false);
        this.title = ko.observable('');

        // Rating system properties (removed employee_ properties)
        this.rating_system_options = ko.observableArray();
        this.rating_system_type = ko.observable();
        this.manager_ratings = ko.observableArray();
        this.manager_rating = ko.observable();
        this.manager_note = ko.observable();
        this.peer_rating = ko.observable();
        this.peer_ratings = ko.observableArray();
        this.peer_note = ko.observable();
        
        // Additional properties for context
        this.objective_plan_id = ko.observable();
        this.logged_in_employee = ko.observable();
        this.manager_id = ko.observable();
        this.employee_id = ko.observable();
        this.is_peer_review = ko.observable(false);
        this.is_manager_review = ko.observable(false);

        this.objective_name = ko.observable('');
        
        // Properties that the template expects
        this.selected_plan = ko.observable({
            rating_method_name: '',
            rating_method_description: '',
            rating_system_id: null,
            objective_plan_id: null
        });
        
        this.rules = ko.observable({
            all_fields_everyone: true,
            all_fields_employee: true,
            name_description: true,
            outcomes_measures: true,
            feedback_manager: true,
            feedback_employee: true,
            peer_feedback: true,
            setup_peer_reviewer: true,
            add_objectives: true,
            linked_company_objectives: false
        });

        // Computed properties
        this.is_logged_in = ko.computed(() => {
            return this.logged_in_employee() === this.employee_id();
        }, this);

        this.is_manager = ko.computed(() => {
            return this.logged_in_employee() === this.manager_id();
        }, this);

        this.is_peer = ko.computed(() => {
            return this.is_peer_review();
        }, this);

        this.full_title = ko.computed(() => {
            const baseTitle = this.title();
            const objName = this.objective_name();
            return objName ? `${baseTitle} (${objName})` : baseTitle;
        }, this);

        this.is_demo_mode = ko.observable(false);
    };

    async btn_save_overall_note() {
        let note;
        let rating;
        
        // For manager reviews
        if (this.is_manager_review()) {
            note = this.manager_note();
            if (this.rating_system_type() === 'multiselect') {
                rating = (this.manager_ratings() || []).toString();
            } else {
                rating = (this.manager_rating() || '').toString();
            }
        } else {
            // This shouldn't happen since we removed employee ratings
            console.error('Unexpected state: not a manager review in btn_save_overall_note');
            return;
        }

        // Add validation
        if (!rating || rating === '') {
            Grape.alerts.alert({
                type: 'warning', 
                message: window.tr('Please provide a rating before saving.')
            });
            return;
        }

        let response = await Grape.alerts.confirm({ 
            type: 'warning',
            title: 'Warning', 
            message: 'Update overall feedback?' 
        });

        if (response) {
            let res = await Grape.fetches.postJSON('/performance_management/objective_plan_feedback/edit', {
                objective_plan_id: this.objective_plan_id(),
                employee_id: this.manager_id(), // Use the employee being reviewed, not logged in employee
                notes: note,
                rating: rating
            });
            
            if (res.status == 'ERROR') {
                Grape.alerts.apiError(res);
            } else if (res.status == 'OK') {
                Grape.alerts.alert({
                    type: 'success', 
                    title: 'Success',
                    message: window.tr('Successfully updated your feedback.')
                });
                // Refresh the data after successful update
                await this.get_overall_feedback();
                this.close();
            } else {
                Grape.alerts.alert({
                    type: 'error', 
                    message: window.tr('An error occurred while updating feedback.')
                });
            }
        }
    }

    async btn_save_overall_note_peer() {
        let note;
        let rating;
        
        if (this.is_peer_review()) {
            note = this.peer_note();
            if (this.rating_system_type() === 'multiselect') {
                rating = (this.peer_ratings() || []).toString();
            } else {
                rating = (this.peer_rating() || '').toString();
            }
        } else {
            console.error('Unexpected state: not a peer review in btn_save_overall_note_peer');
            return;
        }

        // Add validation
        if (!rating || rating === '') {
            Grape.alerts.alert({
                type: 'warning', 
                title: 'Warning',
                message: window.tr('Please provide a rating before saving.')
            });
            return;
        }

        let response = await Grape.alerts.confirm({ 
            type: 'warning',
            title: 'Warning', 
            message: 'Update overall feedback?' 
        });
        
        if (response) {
            let res = await Grape.fetches.postJSON('/performance_management/objective_plan_feedback_peer/edit', {
                objective_plan_id: this.objective_plan_id(),
                employee_id: this.logged_in_employee(),
                notes: note,
                rating: rating,
                rating_system_id: this.selected_plan().rating_system_id
            });
            
            if (res.status == 'ERROR') {
                Grape.alerts.apiError(res);
            } else if (res.status == 'OK') {
                Grape.alerts.alert({
                    type: 'success', 
                    title: 'Success', 
                    message: window.tr('Successfully updated your feedback.')
                });
                // Refresh the data after successful update
                await this.get_overall_feedback();
                this.close();
            } else {
                Grape.alerts.alert({
                    type: 'error', 
                    message: window.tr('An error occurred while updating feedback.')
                });
            }
        }
    }

    async get_overall_feedback() {
        // Get rating system options
        try {
            if (this.objective_plan_id()) 
            {
                // First get the rating system ID from the objective plan
                let plan_res = await Grape.fetches.getJSON('/api/record', {
                    table: 'objective_plan',
                    schema: 'perfm_manage',
                    filter: [{
                        field: 'objective_plan_id',
                        operand: '=',
                        value: this.objective_plan_id()
                    }]
                });

                if (plan_res.status != 'ERROR' && plan_res.records && plan_res.records.length > 0) 
                {
                    let plan_data = plan_res.records[0];
                    let rating_system_id = plan_data.rating_system_id;

                    // Get rating system type
                    let rating_system_res = await Grape.fetches.getJSON('/api/record', {
                        table: 'rating_system',
                        schema: 'perfm_manage',
                        filter: [{
                            field: 'rating_system_id',
                            operand: '=',
                            value: rating_system_id
                        }]
                    });

                    let rating_method_name = '';
                    let rating_method_description = '';
    
                    if (rating_system_res.status != 'ERROR' && rating_system_res.records && rating_system_res.records.length > 0) {
                        this.rating_system_type(rating_system_res.records[0].type);
                        rating_method_name = rating_system_res.records[0].name; // Get name from rating_system
                        rating_method_description = rating_system_res.records[0].description; // Get description from rating_system
                    }

                    // Update selected_plan with the data
                    this.selected_plan({
                        rating_method_name: rating_method_name,
                        rating_method_description: rating_method_description,
                        rating_system_id: rating_system_id,
                        objective_plan_id: this.objective_plan_id()
                    });

                    // Get rating system options
                    let res = await Grape.fetches.getJSON('/api/record', {
                        table: 'rating_system_option',
                        schema: 'perfm_manage',
                        filter: [{
                            field: 'rating_system_id',
                            operand: '=',
                            value: rating_system_id
                        }]
                    });
                    
                    if (res.status != 'ERROR')
                        this.rating_system_options(res.records);
                }
            } else {
                return;
            }

            // Get peer rating data
            let peerRes = await Grape.fetches.getJSON('/api/record', {
                table: 'peer_rating',
                schema: 'perfm_manage',
                filter: [{
                    field: 'objective_plan_id',
                    operand: '=',
                    value: this.objective_plan_id()
                }, {
                    field: 'employee_id',
                    operand: '=',
                    value: this.logged_in_employee()
                }]
            });

            if (peerRes.status != 'ERROR' && peerRes.records && peerRes.records.length > 0) {
                let peerRecord = peerRes.records[0];
                this.peer_note(peerRecord.notes || '');
                
                if (this.rating_system_type() === 'multiselect') {
                    let rating_array = peerRecord.rating ? 
                        peerRecord.rating.split(',').map(item => parseInt(item.trim(), 10)) : [];
                    this.peer_ratings(rating_array);
                } else if (this.rating_system_type() === 'single-select') {
                    let rating = parseInt(peerRecord.rating);
                    this.peer_rating(rating);
                } else {
                    this.peer_rating(peerRecord.rating);
                }
            }

        } catch (error) {
            console.error(error);
        }

        try {
            if (this.objective_plan_id()) {
                if (!this.manager_id()) return;
                

                let res = await Grape.fetches.getJSON('/api/record', {
                    table: 'emp_manager_rating',
                    schema: 'perfm_manage',
                    filter: [{
                        field: 'objective_plan_id',
                        operand: '=',
                        value: this.objective_plan_id()
                    }, {
                        field: 'employee_id',
                        operand: '=',
                        value: this.manager_id() // This is correct - it's the manager giving the rating
                    }]
                });
            
            
                if (res.status != 'ERROR' && res.records && res.records.length > 0) {
                    let managerRecord = res.records[0];

                    this.manager_note(managerRecord.notes || '');

                    if (this.rating_system_type() === 'multiselect') {
                        let rating_array = managerRecord.rating ? 
                            managerRecord.rating.split(',').map(item => parseInt(item.trim(), 10)) : [];
                        this.manager_ratings(rating_array);
                    } else if (this.rating_system_type() === 'single-select') {
                        let rating = parseInt(managerRecord.rating);
                        this.manager_rating(rating);
                    } else {
                        this.manager_rating(managerRecord.rating);
                    }
                }
            }
        } catch (error) {
            console.error('Error loading manager feedback:', error);
        }
        
        Utils.refresh_data_visible_roles();
    }

    close() {
        this.dialog.close();
    }
}

class RatingAndFeedbackPage {
    constructor(bindings) {
        this.viewModel = new RatingAndFeedbackViewModel(this);
        this.bindings = bindings;
    };

    async init() {
        Utils.refresh_data_visible_roles();
        this.viewModel.title('Peer feedback for: ');
        
        // Set logged in employee
        this.viewModel.logged_in_employee(Utils.current_employee_id());


        if (this.bindings.peer && this.bindings.data) {
            let employee_name = this.bindings.data.firstname_of_review_employee + ' ' + this.bindings.data.surname_of_review_employee;
            this.viewModel.title(`Peer feedback for: ${employee_name}`);
            this.viewModel.is_peer_review(true);
            
            // Set context data from the view
            this.viewModel.objective_plan_id(this.bindings.data.objective_plan_id);
            this.viewModel.employee_id(this.bindings.data.review_employee);
            this.viewModel.manager_id(this.bindings.data.manager_id);
            this.viewModel.objective_name(this.bindings.data.objective_plan_name || '');
        }



        if (this.bindings.peer && !this.bindings.data)
        {
            // Example data for peer review demo
            let employee_name = 'Demo employee';
            this.viewModel.title(`Peer feedback for: ${employee_name}`);
            this.viewModel.is_peer_review(true);
            this.viewModel.is_demo_mode(true); // Mark as demo mode
            
            // Set example context data
            this.viewModel.objective_plan_id(1); // Example objective plan ID
            this.viewModel.employee_id(100); // Example employee being reviewed
            this.viewModel.manager_id(200); // Example manager ID
            this.viewModel.objective_name('Demo objective plan');
            
            // Set example rating system data
            this.viewModel.selected_plan({
                rating_method_name: 'Example Rating Method',
                rating_method_description: 'This is a demonstration rating system',
                rating_system_id: 1,
                objective_plan_id: 1
            });
            
            this.viewModel.rating_system_type('single-select');
            this.viewModel.rating_system_options([
                { rating_system_option_id: 1, symbol: '⭐', name: 'Excellent' },
                { rating_system_option_id: 2, symbol: '✓', name: 'Good' },
                { rating_system_option_id: 3, symbol: '→', name: 'Satisfactory' },
                { rating_system_option_id: 4, symbol: '✗', name: 'Needs Improvement' }
            ]);
        }

         if (this.bindings.manager && this.bindings.data) 
        {
            let employee_name = this.bindings.data.firstname_of_review_employee + ' ' + this.bindings.data.surname_of_review_employee;
            this.viewModel.title(`Manager feedback for: ${employee_name}`);
            this.viewModel.is_manager_review(true);
            
            // Set context data from the view
            this.viewModel.objective_plan_id(this.bindings.data.objective_plan_id);
            this.viewModel.employee_id(this.bindings.data.review_employee);
            this.viewModel.manager_id(this.bindings.data.manager_id);
            this.viewModel.objective_name(this.bindings.data.objective_plan_name || '');
        }


        if (this.bindings.manager && !this.bindings.data)
        {
            // Example data for manager review demo
            let employee_name = 'Demo Employee';
            this.viewModel.title(`Manager feedback for: ${employee_name}`);
            this.viewModel.is_manager_review(true);
            this.viewModel.is_demo_mode(true); // Mark as demo mode
            
            // Set example context data
            this.viewModel.objective_plan_id(2); // Example objective plan ID
            this.viewModel.employee_id(101); // Example employee being reviewed
            this.viewModel.manager_id(Utils.current_employee_id()); // Current user is the manager
            this.viewModel.objective_name('Demo objective plan');
            
            // Set example rating system data
            this.viewModel.selected_plan({
                rating_method_name: 'Manager Performance Rating',
                rating_method_description: 'Comprehensive evaluation of employee performance',
                rating_system_id: 2,
                objective_plan_id: 2
            });
            
            this.viewModel.rating_system_type('single-select');
            this.viewModel.rating_system_options([
                { rating_system_option_id: 5, symbol: '5', name: 'Outstanding - Consistently exceeds expectations' },
                { rating_system_option_id: 4, symbol: '4', name: 'Exceeds Expectations - Frequently surpasses goals' },
                { rating_system_option_id: 3, symbol: '3', name: 'Meets Expectations - Achieves all required goals' },
                { rating_system_option_id: 2, symbol: '2', name: 'Partially Meets - Some improvement needed' },
                { rating_system_option_id: 1, symbol: '1', name: 'Does Not Meet - Significant improvement required' }
            ]);
        }

        try {
            // Load companies
            let res = await Grape.fetches.getJSON('/api/record', {
                table: 'company',
                schema: 'public'
            });
            if (res.status != 'ERROR')
                this.viewModel.companies(res.records);
        }
        catch (error) {
            console.error(error);
            Grape.alerts.alert({ type: 'error', title: 'error', message: 'An error occurred while loading companies' });
        }

        // Load rating system and existing feedback
        if (this.viewModel.objective_plan_id()) {
            await this.viewModel.get_overall_feedback();
        }

        // Check if we're in edit mode (data provided in bindings)
        if (this.bindings && this.bindings.data && this.bindings.data.length > 0) {
            let peer_group_data = this.bindings.data[0]; // Use first record for peer group info
            
            this.viewModel.title('Manager feedback and rating for:');
            await this.updateData();
        }
    }

    async updateData() {
        if (this.viewModel.selected_company()) {
            try {
                let res = await Grape.fetches.getJSON('/api/record', {
                    table: 'v_department_company',
                    schema: 'savanna',
                    filter: [{
                        field: 'company_id',
                        operand: '=',
                        value: this.viewModel.selected_company()
                    }]
                });
                if (res.status != 'ERROR')
                    this.viewModel.departments(res.records);
            }
            catch (error) {
                console.error(error);
                Grape.alerts.alert({ type: 'error', title: 'error', message: 'An error occurred while loading departments' });
            }
        }
    }

    close() {
        // Close dialog method
        if (this.dialog)
            this.dialog.close();
    }
}

export default {
    name: 'RatingAndFeedback',
    dialog_class: RatingAndFeedbackPage,
    template: template,
    template_filename: 'translation/dialogs/rating_and_feedback',
    provider: 'ps'
};